import React, { FC } from 'react';
import { Controller, Control } from 'react-hook-form';
import { Upload } from 'antd';
import {
  UploadOutlined, DeleteOutlined, LoadingOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { MedicalHistoryValues } from '../types/flow';
import {
  FormInput, FormTextArea, FLabel, Radio, Checkbox, SectionDivider,
} from '../../assessment-portal/components/form-fields';
import YesNoButtons from '../../assessment-portal/components/yes-no-buttons';
import {
  YES_NO_OPTIONS,
  SPECIALIST_TYPE_OPTIONS,
  CONSULT_REASON_OPTIONS,
  DIAGNOSIS_DECLINED_OPTIONS,
  PREVIOUS_DIAGNOSIS_OPTIONS,
  EXCLUSIVE_DIAGNOSIS_OPTIONS,
  ACCEPTED_FILE_TYPES,
  CURRENT_CONCERNS_OPTIONS,
} from '../data/constants';

interface ClinicalHistoryFormProps {
  control: Control<MedicalHistoryValues>;
  values: MedicalHistoryValues;
  setValue: (field: keyof MedicalHistoryValues, value: any) => void;
  toggleArr: (field: keyof MedicalHistoryValues, item: string, exclusive?: string[]) => void;
  handlePrevReportUpload: (info: UploadChangeParam) => void;
  isUploading: boolean;
  uploadAction: string;
  uploadHeaders: Record<string, string>;
}

const ClinicalHistoryForm: FC<ClinicalHistoryFormProps> = ({
  control,
  values,
  setValue,
  toggleArr,
  handlePrevReportUpload,
  isUploading,
  uploadAction,
  uploadHeaders,
}) => (
  <>
    <h2 className="text-xl font-bold mb-1 text-brand-dark">Medical History — Part 2 of 4</h2>
    <p className="text-slate-500 text-sm mb-6">Clinical history and previous assessments.</p>

    <div className="space-y-5">
      <SectionDivider>Clinical History</SectionDivider>

      <div className="adhd-mq-card">
        <FLabel hint="Select all that apply">Have you previously seen a specialist?</FLabel>
        <div className="flex flex-col gap-2">
          {SPECIALIST_TYPE_OPTIONS.map((o) => (
            <Checkbox
              key={o}
              label={o}
              selected={values.seenSpecialist.includes(o)}
              onClick={() => toggleArr('seenSpecialist', o)}
            />
          ))}
        </div>
      </div>

      <div className="adhd-mq-card">
        <FLabel isRequired>Have you seen a psychiatrist within the last 12 months (including in a hospital)?</FLabel>
        <div className="hidden md:flex gap-6 mb-2">
          {YES_NO_OPTIONS.map((o) => (
            <Radio
              key={o}
              label={o}
              selected={values.seenPsychiatrist12m === o}
              onClick={() => setValue('seenPsychiatrist12m', o)}
            />
          ))}
        </div>
        <div className="md:hidden mb-2">
          <YesNoButtons
            value={values.seenPsychiatrist12m}
            onChange={(v) => setValue('seenPsychiatrist12m', v)}
            options={YES_NO_OPTIONS}
            ariaLabel="Have you seen a psychiatrist within the last 12 months?"
          />
        </div>
        {values.seenPsychiatrist12m === 'Yes' && (
          <Controller
            name="psychiatristDetail"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Estimate month and name of doctor or hospital" />}
          />
        )}
      </div>

      <div className="adhd-mq-card">
        <FLabel isRequired hint="More than one option can be selected">Reason for Consult</FLabel>
        <div className="flex flex-col gap-2">
          {CONSULT_REASON_OPTIONS.map((o) => (
            <Checkbox
              key={o}
              label={o}
              selected={values.reasonForConsult.includes(o)}
              onClick={() => toggleArr('reasonForConsult', o)}
            />
          ))}
        </div>
        {values.reasonForConsult.includes('Other') && (
          <Controller
            name="reasonOther"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormTextArea ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Please describe your reason for consult" rows={2} className="mt-2" />}
          />
        )}
      </div>

      <div className="adhd-mq-card">
        <FLabel isRequired>Have you been declined an ADHD diagnosis in the past?</FLabel>
        <div className="flex flex-col gap-2">
          {DIAGNOSIS_DECLINED_OPTIONS.map((o) => (
            <Checkbox
              key={o}
              label={o}
              selected={values.declinedDiagnosis === o}
              onClick={() => setValue('declinedDiagnosis', o)}
            />
          ))}
        </div>
        {(values.declinedDiagnosis === 'No (previous diagnosis — requesting a review)' || values.declinedDiagnosis === 'Yes (I want a second opinion)') && (
          <div className="mt-3 p-3 rounded-xl border border-blue-200 bg-blue-50 text-xs text-blue-700">
            <p className="font-semibold mb-1">Previous report / letter with diagnosis</p>
            <p className="mb-2">Upload any supporting reports or letters to help expedite your assessment.</p>
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              {values.prevReportFile ? (
                <div className="flex items-center gap-2 flex-1">
                  <CheckCircleOutlined className="text-green-500" />
                  <span className="text-blue-600">{values.prevReportFile.fileName}</span>
                  <button type="button" onClick={() => setValue('prevReportFile', null)} className="text-red-400 hover:text-red-600 flex-shrink-0 min-h-yn-btn">
                    <DeleteOutlined />
                  </button>
                </div>
              ) : (
                <Upload
                  accept={ACCEPTED_FILE_TYPES}
                  action={uploadAction}
                  headers={uploadHeaders}
                  showUploadList={false}
                  onChange={handlePrevReportUpload}
                >
                  <button type="button" className="w-full md:w-auto px-3 py-2.5 rounded-lg text-white text-xs font-semibold bg-brand flex items-center justify-center gap-1.5 cursor-pointer min-h-yn-btn">
                    {isUploading ? <LoadingOutlined /> : <UploadOutlined />}
                    {isUploading ? 'Uploading...' : 'Browse'}
                  </button>
                </Upload>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="adhd-mq-card">
        <FLabel isRequired hint="Select all that apply">Have you previously been formally diagnosed with any of the following?</FLabel>
        <div className="flex flex-col gap-2">
          {PREVIOUS_DIAGNOSIS_OPTIONS.map((o) => (
            <Checkbox
              key={o}
              label={o}
              selected={values.prevDiagnosis.includes(o)}
              onClick={() => toggleArr('prevDiagnosis', o, [...EXCLUSIVE_DIAGNOSIS_OPTIONS])}
            />
          ))}
        </div>
        {values.prevDiagnosis.includes('Other') && (
          <Controller
            name="otherDiagnosis"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Please specify other diagnosis" className="mt-2" />}
          />
        )}
      </div>

      <SectionDivider>Current Concerns</SectionDivider>

      <div className="adhd-mq-card">
        <FLabel isRequired hint="Select all that apply">Are any of the following currently affecting you or relevant to your care?</FLabel>
        <div className="flex flex-col gap-2">
          {CURRENT_CONCERNS_OPTIONS.map((o) => (
            <Checkbox
              key={o}
              label={o}
              selected={values.consultReasonNotReferral.includes(o)}
              onClick={() => toggleArr('consultReasonNotReferral', o, [...EXCLUSIVE_DIAGNOSIS_OPTIONS])}
            />
          ))}
        </div>
        {values.consultReasonNotReferral.includes('Other') && (
          <Controller
            name="consultReasonNotReferralOther"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Please specify other reason" className="mt-2" />}
          />
        )}
      </div>
    </div>
  </>
);

export default ClinicalHistoryForm;
